import React, { useEffect, useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import MaterialInput from '../MaterialInput';
import { DATE_FORMAT_PICKER } from 'src/config';
import styled from 'styled-components';
import { PickersCalendarHeader, PickersDay, PickersLayout } from '@mui/x-date-pickers';
import { ActionBar, Toolbar } from './comps';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import _ from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import { Calendar } from 'src/assets/dynamicSvg';
import { useStateSelector } from 'src/redux';

const StyledPickersLayout = styled(PickersLayout)`
  .MuiDayCalendar-weekDayLabel {
    color: ${({ theme }) => theme.colors.cflowerBlue} !important;
  }
`;

const StyledHeader = styled(PickersCalendarHeader)`
  .MuiPickersCalendarHeader-label {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
  .MuiSvgIcon-root {
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }
`;

const StyledDay = styled(PickersDay)`
  font-weight: 500 !important;

  &.MuiPickersDay-root {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
  &.Mui-selected {
    background: ${({ theme }) => theme.colors.gradient} !important;
  }

  .adornment {
    display: flex;
    &__value {
      width: 35px;
      &__btn {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        bottom: 0;
        background: ${({ theme }) => theme.colors.cultured};
      }
    }
  }

  &.MuiDateCalendar-root {
    border: 2px solid red !important;
  }
`;

interface InputProps extends React.ComponentProps<typeof MaterialInput> {
  ['data-cy']?: string;
}

interface MaterialDatePickerProps extends Omit<React.ComponentProps<typeof DatePicker>, 'value' | 'onChange'> {
  value: string | null | undefined;
  onChange: (date: string | null) => void;
  inputProps?: InputProps;
  popperProps?: Record<string, any>;
  disableBodyScrollLock?: boolean;
}

const MaterialDatePicker: React.FC<MaterialDatePickerProps> = ({
  value,
  inputProps,
  onChange,
  popperProps,
  disableBodyScrollLock = false,
  ...rest
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [open, setOpen] = useState(false);
  const hadValueOnOpen = useRef(false);

  const { onClose: externalOnClose, onOpen: externalOnOpen, open: controlledOpen, ...restProps } = rest as any;
  const isOpenControlled = typeof controlledOpen === 'boolean';
  const pickerOpen = isOpenControlled ? controlledOpen : open;

  const handleOnDateChange = (date: Moment | null) => {
    onChange(date ? date.toISOString() : null);
  };

  const handlePeriodSelect = (date: Moment) => {
    handleOnDateChange(date);
    if (isOpenControlled) {
      externalOnClose?.();
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!pickerOpen || !disableBodyScrollLock || typeof document === 'undefined') {
      return;
    }

    const targets = [document.body, document.documentElement];
    const clearScrollLockStyles = () => {
      targets.forEach((target) => {
        if (target.style.overflow === 'hidden') {
          target.style.overflow = '';
        }

        if (target.style.paddingRight) {
          target.style.paddingRight = '';
        }
      });
    };

    clearScrollLockStyles();

    const observers = targets.map((target) => {
      const observer = new MutationObserver(clearScrollLockStyles);
      observer.observe(target, { attributes: true, attributeFilter: ['style'] });
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [disableBodyScrollLock, pickerOpen]);

  return (
    <DesktopDatePicker
      open={pickerOpen}
      onOpen={() => {
        hadValueOnOpen.current = !!value;
        if (!isOpenControlled) {
          setOpen(true);
        }
        externalOnOpen?.();
      }}
      onClose={() => {
        if (!isOpenControlled) {
          setOpen(false);
        }
        externalOnClose?.();
      }}
      value={value ? moment(value) : null}
      onChange={handleOnDateChange}
      format={DATE_FORMAT_PICKER}
      slots={{
        textField: MaterialInput,
        toolbar: Toolbar,
        actionBar: ActionBar,
        day: StyledDay,
        calendarHeader: StyledHeader,
        openPickerIcon: Calendar,
        layout: StyledPickersLayout,
      }}
      slotProps={{
        textField: {
          ...inputProps,
        },
        actionBar: {
          onPeriodSelect: handlePeriodSelect,
        } as any,
        popper: {
          ...popperProps,
          placement: popperProps?.placement || 'auto',
          sx: [
            {
              '.MuiPickersLayout-root': {
                background: colors.white,
              },
            },
            ...(Array.isArray(popperProps?.sx) ? popperProps.sx : popperProps?.sx ? [popperProps.sx] : []),
          ],
        },
        openPickerButton: {
          sx: {
            marginRight: 1,
          },
        },
        toolbar: {
          toolbarFormat: DATE_FORMAT_PICKER,
          showToolbar: hadValueOnOpen.current,
        } as any,
      }}
      {...restProps}
    />
  );
};
export default MaterialDatePicker;
